import { useState } from 'react';
import {
  DropDownValues,
  IDiscountFieldProps,
  articlesForDiscount,
  dropDownOptions,
  getValidity,
  validateDiscount,
} from '../MarginCartUtils';
import { defaultTo, get } from 'lodash';
import { OnChangeValue } from 'react-select';
import { IAccessoryList, IAuxiliaryCalculationsInput, INextArticle, mapVatPricesForNext } from 'next-common';
import { calculateVatForElCars } from '../../../../utils';
import { BosDropDownOptions, CartPrices, formatNumberInput, InputType, TMessageBarSeverity } from 'types';
import { IconType, MessageBar, PivotField } from '@repo/ui/components';
import { ArticleType } from 'sales-common';

interface ICalculateDiscountProps extends IDiscountFieldProps {
  articles: INextArticle[];
  vatPrices: CartPrices;
  setVatPrices(vatPrices: CartPrices): void;
  carType: string;
  onUpdateArticles?(value: IAccessoryList, customerAdvantage?: boolean, discountArticles?: INextArticle[]): void;
}

export const CalculateDiscount = (props: ICalculateDiscountProps) => {
  const [inputType, setInputType] = useState<InputType>(InputType.INTEGER);
  const [hasChanged, setHasChanged] = useState<boolean>(false);

  const getDiscountValue = (marginObject: string = 'activeMargin') => {
    if (inputType === InputType.INTEGER) {
      return get(props, `${marginObject}.discountValue`, 0) || get(props, `${marginObject}.campaignDiscount`, 0);
    }
    return get(props, `${marginObject}.campaignDiscountPercentage`, 0);
  };

  const handleOnChangeDropDown = (dropDownValue: OnChangeValue<BosDropDownOptions, false>): void => {
    switch (dropDownValue.label) {
      case DropDownValues.KR:
        setInputType(InputType.INTEGER);
        break;
      case DropDownValues.PERCENTAGE:
        setInputType(InputType.FLOAT);
        break;
    }
  };

  const onDiscountChange = (
    input: string | number,
    dropDownValue?: OnChangeValue<BosDropDownOptions, false>,
    formatted?: string,
    event?: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ): void => {
    if (event.type !== 'blur') return;

    const numericInput = Number(input);
    const isKr = inputType === InputType.INTEGER;

    setHasChanged(getDiscountValue('latestMargin') !== numericInput);

    const valid = validateDiscount(numericInput, dropDownValue?.label as DropDownValues, 35);
    props.setIsValid(valid);

    const auxillaryInput: IAuxiliaryCalculationsInput = {
      ...(isKr ? { discount: numericInput } : { discountPercentage: numericInput }),
      importerDiscount: props.activeMargin?.campaignImporter,
      importerPercentage: props.activeMargin?.campaignImporterPercentage,
    };

    let discountArticles = articlesForDiscount(props.articles, auxillaryInput, props.order);

    const excludeDiscountType = isKr ? ArticleType.DISCOUNT_PERCENTAGE : ArticleType.CUSTOMERADVANTAGE;
    discountArticles = discountArticles.filter((article) => article.articleType !== excludeDiscountType);

    const excludeImporterType =
      auxillaryInput?.importerPercentage > 0
        ? ArticleType.IMPORTCONTRIBUTION
        : ArticleType.IMPORTCONTRIBUTION_PERCENTAGE;
    discountArticles = discountArticles.filter((article) => article.articleType !== excludeImporterType);

    console.log(`Discount articles for ${isKr ? 'KR' : 'percentage'}`, discountArticles);

    const vatCalculation = calculateVatForElCars(discountArticles, props.carType);
    const discount = vatCalculation?.calculatedDiscountOnVehicle;

    props?.setActiveMargin({
      ...get(props, 'activeMargin', []),
      campaignDiscount: Math.round(discount || 0),
      discountValue: isKr ? numericInput : Math.round(discount || 0),
      campaignDiscountPercentage: vatCalculation?.customerAdvantagePercentage,
      marginValue: vatCalculation?.marginAmount,
      marginPercentage: vatCalculation?.marginPercentage,
    });

    if (!valid) return;

    props.setVatPrices(mapVatPricesForNext(vatCalculation) as any);
    if (typeof props.onUpdateArticles === 'function') {
      props.onUpdateArticles(null, true, discountArticles);
    }
  };

  return (
    <div className="flex flex-col w-3/4 items-end gap-2">
      <label htmlFor="add-discount" className="w-full">
        Legg til rabatt (inkl. mva)
      </label>
      <PivotField
        dropDownOptions={dropDownOptions}
        onChangeDropDown={handleOnChangeDropDown}
        onChange={(input, drop, formatted, event) => onDiscountChange(input, drop, formatted, event)}
        value={`${getDiscountValue()}`}
        disabled={false}
        type={inputType}
        decimals={2}
        {...getValidity(
          hasChanged,
          props?.vatPrices?.discount > 0 ? props.isValid : null,
          props.vehicleConfiguration,
          true
        )}
      />
      {defaultTo(props.pimSnapshot?.customerAdvantageExcludingVat, 0) !== 0 && (
        <MessageBar
          severity={TMessageBarSeverity.INFORMATION}
          className="py-1 items-center gap-1 px-3"
          alertIcon={{ icon: IconType.Info, size: 20, fill: 'var(--color-blue)' }}
          alertIconClassName="mt-0"
        >
          <span className="text-sm/[170%] font-semibold">
            {`Markedsført rabatt: ${
              formatNumberInput(
                Math.abs(get(props, 'pimSnapshot.customerAdvantageExcludingVat', 0))?.toString(),
                inputType,
                2
              )?.formatted || 0
            } kr`}
          </span>
        </MessageBar>
      )}
    </div>
  );
};
